import { Component, createRef, type ReactNode, useState } from "react";

import { BTDropdown } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";

import { isRouteLocationState } from "utilities/flatRoutes/routes";
import { addCloseOnScroll, removeCloseOnScroll } from "utilities/helpers";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import {
    BTFormattedPlural,
    type IBTFormattedPluralProps,
} from "commonComponents/btWrappers/BTFormattedPlural/BTFormattedPlural";
import { BTIconCaretSmallDown } from "commonComponents/btWrappers/BTIcon";
import { BTLink, BTLinkRelative } from "commonComponents/btWrappers/BTLink/BTLink";
import { BTMenu } from "commonComponents/btWrappers/BTMenu/BTMenu";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTSpin } from "commonComponents/btWrappers/BTSpin/BTSpin";
import {
    type IRelatedContextMenuItem,
    type IRelatedItemContextMenuEntity,
} from "commonComponents/entity/relatedItem/RelatedItemContextMenu/RelatedItemContextMenu.api.types";

import "./RelatedItemContextMenu.less";

interface IRelatedItemContextMenuProps extends IBTFormattedPluralProps {
    entityId: number;
    builderId: number;
    jobId: number;
    /** Pass in a function to load the menu items */
    getContextMenu: (entityId: number, builderId: number) => Promise<IRelatedItemContextMenuEntity>;
    /** Pass in a function to display title. If not provided title string will be used */
    renderTitle?: (item: IRelatedContextMenuItem) => ReactNode;
    className?: string;
}

interface IRelatedItemContextMenuState {
    menuItems?: IRelatedContextMenuItem[];
}

export class RelatedItemContextMenu extends Component<
    IRelatedItemContextMenuProps,
    IRelatedItemContextMenuState
> {
    static defaultProps = {
        one: "Item",
        other: "Items",
    };

    state: Readonly<IRelatedItemContextMenuState> = {
        menuItems: undefined,
    };

    private lazyLoadMenu = async (isVisible: boolean) => {
        const { getContextMenu, entityId, builderId } = this.props;
        if (isVisible) {
            const contextMenu = await getContextMenu(entityId, builderId);

            if (contextMenu.contextMenuItems.length > 0) {
                this.setState({ menuItems: contextMenu.contextMenuItems });
            } else {
                throw new Error("No items were found for this entity");
            }
        }
    };

    private getMenu = () => {
        if (this.state.menuItems) {
            const { renderTitle } = this.props;
            return (
                <BTMenu testid="relatedItemMenu">
                    {this.state.menuItems.map((item) => (
                        <BTMenu.Item
                            testid={`RelatedMenuItem- ${item.id}`}
                            key={item.id}
                            className={cn("RelatedMenuItem", this.props.className, {
                                readOnly: item.isReadonly,
                            })}
                        >
                            {item.isReadonly ? (
                                renderTitle ? (
                                    renderTitle(item)
                                ) : (
                                    item.title
                                )
                            ) : (
                                <>
                                    {isRouteLocationState(item.linkRoute) ? (
                                        <BTLink to={item.linkRoute}>
                                            {renderTitle ? renderTitle(item) : item.title}
                                        </BTLink>
                                    ) : (
                                        <BTLinkRelative to={item.linkRoute}>
                                            {renderTitle ? renderTitle(item) : item.title}
                                        </BTLinkRelative>
                                    )}
                                </>
                            )}
                        </BTMenu.Item>
                    ))}
                </BTMenu>
            );
        }
        return (
            <BTMenu testid="relatedItemLoadingMenu">
                <BTMenu.Item testid="RelatedMenuItem-loading">
                    <BTRow align="middle" justify="center">
                        <BTCol>
                            <BTSpin />
                        </BTCol>
                    </BTRow>
                </BTMenu.Item>
            </BTMenu>
        );
    };

    render() {
        const { entityId, value } = this.props;

        return (
            <LazyLoadContextMenu
                menu={this.getMenu()}
                lazyLoadMenu={this.lazyLoadMenu}
                entityId={entityId}
                className="RelatedItemContextMenu"
            >
                <span>
                    {value} Related <BTFormattedPlural {...this.props} />
                </span>
            </LazyLoadContextMenu>
        );
    }
}

interface ILazyLoadContextMenuProps {
    children: React.ReactNode;
    menu: JSX.Element;
    lazyLoadMenu: (isVisible: boolean) => Promise<void>;
    entityId: number;
    className?: string;
    testId?: string;
}

export const LazyLoadContextMenu: React.FC<ILazyLoadContextMenuProps> = (props) => {
    const { menu, lazyLoadMenu, children, entityId, className, testId } = props;
    const [visible, setVisible] = useState(false);
    const dropdownMenuSpan = createRef<HTMLDivElement>();

    const onVisibleChange = (isVisible: boolean) => {
        setVisible(isVisible);
        void lazyLoadMenu(isVisible);

        if (dropdownMenuSpan.current) {
            if (isVisible) {
                addCloseOnScroll(dropdownMenuSpan.current, () => setVisible(false), true);
            } else {
                removeCloseOnScroll(dropdownMenuSpan.current, () => setVisible(false), true);
            }
        }
    };

    return (
        <>
            <span ref={dropdownMenuSpan} style={{ position: "absolute" }} />
            <BTDropdown
                testid={
                    testId ? `${testId}_${entityId}-dropdown` : `contextMenu_${entityId}-dropdown`
                }
                overlay={menu}
                trigger={["click"]}
                onVisibleChange={onVisibleChange}
                visible={visible}
                className={cn(className, "BTDropdown")}
                overlayStyle={{ zIndex: 2 }}
            >
                <BTButtonDeprecated
                    type="link"
                    data-testid={testId ? `${testId}_${entityId}` : `contextMenu_${entityId}`}
                >
                    {children}
                    <BTIconCaretSmallDown className="caretDown" />
                </BTButtonDeprecated>
            </BTDropdown>
        </>
    );
};
